import { useRef, useState, useCallback, MutableRefObject } from 'react';
import useResizeScroll from './useResizeScroll';

type UseSliding = {
  scrollLeft: () => void;
  scrollRight: () => void;
  noScroll: boolean;
  scrollState: { isLeft: boolean; isRight: boolean };
  innerRef: MutableRefObject<HTMLDivElement | null>;
  outerRef: MutableRefObject<HTMLDivElement | null>;
  scrollRef: MutableRefObject<HTMLDivElement | null>;
  resizeCb: () => void;
};

const SCROLL_OFFSET = 5;

const useSliding = (
  deps?: unknown[],
  scrollDistance?: number,
  forceRecalculation?: unknown
): UseSliding => {
  const innerRef = useRef<HTMLDivElement>(null);
  const outerRef = useRef<HTMLDivElement>(null);
  const [noScroll, setNoScroll] = useState<boolean>(false);
  const [scrollState, setScrollState] = useState<{ isLeft: boolean; isRight: boolean }>({
    isLeft: false,
    isRight: false
  });

  const resizeCb = useCallback(() => {
    if (outerRef?.current && innerRef?.current) {
      setNoScroll(outerRef.current.clientWidth >= innerRef.current.clientWidth);
    }
  }, []);

  const scrollCb = useCallback(
    (container?: HTMLElement | null) => {
      if (!container || !innerRef.current) return;
      if (container.scrollLeft <= SCROLL_OFFSET) {
        setScrollState({ isLeft: true, isRight: false });
      } else if (
        container.scrollLeft + container.clientWidth + SCROLL_OFFSET >=
        innerRef.current.clientWidth
      ) {
        setScrollState({ isLeft: false, isRight: true });
      } else {
        setScrollState({ isLeft: false, isRight: false });
      }
    },
    [innerRef, forceRecalculation]
  );

  const scrollRef = useResizeScroll<HTMLDivElement>({
    resizeCb,
    scrollCb,
    deps: deps ? [innerRef, ...deps] : [innerRef],
    updateOnResize: true
  });

  const scrollLeft = useCallback(() => {
    scrollRef?.current?.scrollBy({
      left: scrollDistance ? scrollDistance * -1 : (scrollRef?.current?.clientWidth || 0) * -1,
      top: 0,
      behavior: 'smooth'
    });
  }, [scrollRef]);

  const scrollRight = useCallback(() => {
    scrollRef?.current?.scrollBy({
      left: scrollDistance || scrollRef?.current?.clientWidth || 0,
      top: 0,
      behavior: 'smooth'
    });
  }, [scrollRef]);

  return {
    scrollLeft,
    scrollRight,
    noScroll,
    scrollState,
    innerRef,
    outerRef,
    scrollRef,
    resizeCb
  };
};

export default useSliding;
